import { useMemo } from 'react';
import { useTranslation } from '../../../redux/TranslationHelper';
import { ComponentRenderMode } from '../../../types/component-skin';
import { adaptImageTextProps, extractHtmlContent } from './ImageText.adapter';

/**
 * ImageText 컴포넌트 비즈니스 로직 훅
 */
export function useImageTextLogic(componentData: any, mode: ComponentRenderMode) {
  // 번역 함수
  const { t } = useTranslation();
  
  // 어댑터를 통해 props 변환 - useMemo로 메모이제이션
  const adaptedProps = useMemo(() => adaptImageTextProps(componentData), [componentData]);
  
  // 속성 추출
  const {
    id,
    content,
    style = {},
    componentProps = {},
    viewMode = 'pc'
  } = adaptedProps;

  // 컴포넌트 속성 추출 - useMemo로 메모이제이션
  const extractedProps = useMemo(() => {
    // 기본 속성 추출
    const imageUrl = componentProps.imageUrl || '';
    const imagePosition = componentProps.imagePosition || 'left';
    const imageWidth = componentProps.imageWidth || '50%';
    const textWidth = componentProps.textWidth || '50%';
    const gap = componentProps.gap || '0%';
    const verticalAlign = componentProps.verticalAlign || 'middle';
    
    // 두 번째 이미지 옵션 (텍스트 대신 이미지 표시)
    const useSecondImage = componentProps.useSecondImage || false;
    const secondImageUrl = componentProps.secondImageUrl || '';
    
    return {
      imageUrl,
      imagePosition,
      imageWidth,
      textWidth,
      gap,
      verticalAlign,
      useSecondImage,
      secondImageUrl
    };
  }, [componentProps]);

  // 모바일 환경 확인
  const isMobile = viewMode === 'mobile' || viewMode === 'tablet';
  
  // 텍스트 내용 추출 - useMemo로 메모이제이션
  const textContent = useMemo(() => {
    // 기본 텍스트 내용
    const defaultText = t('샘플 텍스트입니다. 내용을 수정해주세요.');
    
    // content가 없으면 기본 텍스트 반환
    if (!content) return defaultText;
    
    // extractHtmlContent 함수를 사용하여 HTML 내용 추출
    return extractHtmlContent(content, viewMode) || defaultText;
  }, [content, viewMode, t]);
  
  // 수직 정렬에 따른 스타일 설정 - useMemo로 메모이제이션
  const secondContainerStyle = useMemo(() => ({
    width: isMobile ? '100%' : extractedProps.textWidth,
    order: extractedProps.imagePosition === 'left' ? 1 : 0,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: extractedProps.verticalAlign === 'top' ? 'flex-start' : 
                   extractedProps.verticalAlign === 'bottom' ? 'flex-end' : 'center',
    height: 'auto'
  }), [isMobile, extractedProps.textWidth, extractedProps.imagePosition, extractedProps.verticalAlign]);
  
  // 컴포넌트 스타일 - useMemo로 메모이제이션
  const componentStyle = useMemo(() => ({
    ...style,
    justifyContent: 'space-between',
    gap: isMobile ? '20px' : extractedProps.gap,
    height: 'auto',
    minHeight: 'unset'
  }), [style, isMobile, extractedProps.gap]);
  
  // 이미지 컨테이너 스타일 - useMemo로 메모이제이션
  const imageContainerStyle = useMemo(() => ({
    width: isMobile ? '100%' : extractedProps.imageWidth,
    order: extractedProps.imagePosition === 'left' ? 0 : 1,
    height: 'auto'
  }), [isMobile, extractedProps.imageWidth, extractedProps.imagePosition]);

  // 스킨 시스템에 맞는 data/actions 구조로 반환
  return {
    data: {
      id,
      content,
      style,
      componentProps,
      viewMode,
      imageUrl: extractedProps.imageUrl,
      imagePosition: extractedProps.imagePosition,
      imageWidth: extractedProps.imageWidth,
      textWidth: extractedProps.textWidth,
      gap: extractedProps.gap,
      verticalAlign: extractedProps.verticalAlign,
      useSecondImage: extractedProps.useSecondImage,
      secondImageUrl: extractedProps.secondImageUrl,
      isMobile,
      textContent,
      componentStyle,
      imageContainerStyle,
      secondContainerStyle
    },
    actions: {
      // 현재 ImageText는 정적 컴포넌트이므로 액션이 없음
      // 필요시 향후 이벤트 핸들러 추가 가능
    }
  };
}

export default useImageTextLogic;